import { useListProvinceContext } from 'components/providers/list-province-provider';
import FormTextInput from 'components/shared/form-text-input';
import {
  GET_LIST_DISTRICTS_OF_PROVINCE,
  GetListDistrictsOfProvinceData,
  GetListDistrictsOfProvinceVariables,
} from 'graphql/map/queries/get-list-districts-of-province';
import {
  GET_LIST_STREETS_OF_DISTRICT,
  GetListStreetsOfDistrictData,
  GetListStreetsOfDistrictVariables,
} from 'graphql/map/queries/get-list-streets-of-district';
import {
  GET_LIST_WARDS_OF_DISTRICT,
  GetListWardsOfDistrictData,
  GetListWardsOfDistrictVariables,
} from 'graphql/map/queries/get-list-wards-of-district';
import { useFlexQuery } from 'hooks';
import { find, isEmpty, map, toString } from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import FormSelect from '../form-select';

interface Props {
  label?: string;
  required?: boolean;
}

const AddressPart = ({ label, required }: Props) => {
  const addressPartForm = useFormContext();
  const { formState, control, setValue } = addressPartForm;
  const { errors } = formState;
  const {
    provinceId: provinceIdError,
    districtId: districtIdError,
    wardId: wardIdError,
    streetId: streetIdError,
    houseNumber: houseNumberError,
  } = errors;
  const provinceIdWatch = useWatch({ control, name: 'provinceId' });
  const districtIdWatch = useWatch({ control, name: 'districtId' });
  const wardIdWatch = useWatch({ control, name: 'wardId' });
  const streetIdWatch = useWatch({ control, name: 'streetId' });
  const { data: provinces } = useListProvinceContext();
  const provinceOptions =
    map(provinces, (province) => ({ value: province.id, label: province.name })) ?? [];
  const { data: getListDistrictsOfProvinceData } = useFlexQuery<
    GetListDistrictsOfProvinceData,
    GetListDistrictsOfProvinceVariables
  >('map', GET_LIST_DISTRICTS_OF_PROVINCE, {
    variables: {
      provinceId: toString(provinceIdWatch),
    },
    skip: isEmpty(provinceIdWatch),
  });
  const { data: getListWardsOfDistrictData } = useFlexQuery<
    GetListWardsOfDistrictData,
    GetListWardsOfDistrictVariables
  >('map', GET_LIST_WARDS_OF_DISTRICT, {
    variables: {
      districtId: districtIdWatch,
    },
    skip: isEmpty(districtIdWatch),
  });
  const { data: getListStreetsOfDistrictData } = useFlexQuery<
    GetListStreetsOfDistrictData,
    GetListStreetsOfDistrictVariables
  >('map', GET_LIST_STREETS_OF_DISTRICT, {
    variables: {
      districtId: toString(districtIdWatch),
    },
    skip: isEmpty(districtIdWatch),
  });
  const districtOptions = map(
    getListDistrictsOfProvinceData?.getListDistrictsOfProvince?.districts,
    (district) => ({
      value: district.id,
      label: district.name,
    }),
  );
  const wardOptions = map(getListWardsOfDistrictData?.getListWardsOfDistrict?.wards, (ward) => ({
    value: ward.id,
    label: ward.name,
  }));
  const streetOptions = map(
    getListStreetsOfDistrictData?.getListStreetsOfDistrict?.streets,
    (street) => ({
      value: street.id,
      label: street.name,
    }),
  );

  return (
    <div className='flex space-x-[24px]'>
      <label className='mt-[10px] w-[150px] min-w-[150px]'>
        <span className=' font-semibold leading-[18px]'>{label ? label : 'Địa chỉ'}</span>
        {required && <span className='ml-[4px] text-alert'>*</span>}
      </label>
      <div className='flex w-full flex-col space-y-[15px]'>
        <div className='grid grid-cols-3 gap-[16px]'>
          <div className='w-full'>
            <FormSelect
              placeHolder
              name='provinceId'
              label='Chọn tỉnh/ thành phố'
              options={provinceOptions}
              onChange={(value) => {
                setValue('province', find(provinceOptions, { value }));
                setValue('districtId', undefined);
                setValue('wardId', undefined);
                setValue('streetId', undefined);
                setValue('district', undefined);
                setValue('ward', undefined);
                setValue('street', undefined);
                setValue('houseNumber', undefined);
              }}
              emptyOptionsText='Không có tuỳ chọn'
              wrapperClassName='h-[40px] px-[12px] py-[9px]'
              optionClassName='h-[32px] px-[12px]'
              popoverClassName='py-[9px]'
              error={provinceIdError}
            />
          </div>
          <div className='w-full'>
            <FormSelect
              placeHolder
              name='districtId'
              options={districtOptions}
              disabled={isEmpty(provinceIdWatch)}
              onChange={(value) => {
                setValue('district', find(districtOptions, { value }));
                setValue('wardId', undefined);
                setValue('streetId', undefined);
                setValue('ward', undefined);
                setValue('street', undefined);
                setValue('houseNumber', undefined);
              }}
              label='Chọn quận/ huyện'
              emptyOptionsText='Không có tuỳ chọn'
              wrapperClassName='h-[40px] px-[12px] py-[9px]'
              optionClassName='h-[32px] px-[12px]'
              popoverClassName='py-[9px]'
              error={districtIdError}
            />
          </div>
          <div className='w-full'>
            <FormSelect
              placeHolder
              name='wardId'
              options={wardOptions}
              disabled={isEmpty(districtIdWatch)}
              onChange={(value) => {
                setValue('ward', find(wardOptions, { value }));
                setValue('houseNumber', undefined);
              }}
              label='Chọn phường/ xã'
              emptyOptionsText='Không có tuỳ chọn'
              wrapperClassName='h-[40px] px-[12px] py-[9px]'
              optionClassName='h-[32px] px-[12px]'
              popoverClassName='py-[9px]'
              error={wardIdError}
            />
          </div>
        </div>
        <div className='grid grid-cols-3 gap-[16px]'>
          <div className='col-span-2'>
            <FormSelect
              name='streetId'
              options={streetOptions}
              disabled={isEmpty(districtIdWatch)}
              onChange={(value) => {
                setValue('street', find(streetOptions, { value }));
                setValue('houseNumber', undefined);
              }}
              placeHolder
              label='Chọn đường'
              emptyOptionsText='Không có tuỳ chọn'
              wrapperClassName='h-[40px] px-[12px] py-[9px]'
              optionClassName='h-[32px] px-[12px]'
              popoverClassName='py-[9px]'
              error={streetIdError}
            />
          </div>
          <div className='col-span-1'>
            <FormTextInput
              name='houseNumber'
              disabled={isEmpty(wardIdWatch) && isEmpty(streetIdWatch)}
              placeHolder
              maxLength={20}
              label='Nhập số nhà'
              error={houseNumberError}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressPart;
